import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import '../../assets/styles/card.css';
import {
  FaMicrochip,
  FaSyncAlt,
  FaBatteryFull,
  FaBatteryHalf,
  FaBatteryQuarter,
  FaBatteryEmpty,
  FaVolumeUp,
  FaRulerCombined,
  FaMapMarkerAlt,
} from 'react-icons/fa';
import BASE_URL from '../../config';

function DeviceInfoCard() {
  const navigate = useNavigate();
  const [device, setDevice] = useState(null);
  const [status, setStatus] = useState(null);
  const [locationStatus, setLocationStatus] = useState(null);
  const [deviceSettings, setDeviceSettings] = useState(null);
  const [chargingStatus, setChargingStatus] = useState(null);
  const [wearingDisplay, setWearingDisplay] = useState(null);
  const [loading, setLoading] = useState(true);

  // --- Fetch device, status, and settings ---
  const fetchDeviceAndStatus = async () => {
    setLoading(true);
    const token = localStorage.getItem('jwt_token');

    if (!token) {
      console.warn('No authentication token found');
      setLoading(false);
      return;
    }

    try {
      // --- 1. Get customer device ---
      const deviceResponse = await axios.post(
        `${BASE_URL}/api/get_cust_devices/`,
        {},
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const deviceData = Array.isArray(deviceResponse.data)
        ? deviceResponse.data[0]
        : deviceResponse.data;

      if (!deviceData || !deviceData.uid) {
        console.warn('No valid device data found.');
        setLoading(false);
        return;
      }

      setDevice(deviceData);

      // --- 2. Request battery update from device ---
      try {
        await axios.post(
          `${BASE_URL}/api/iot/twin/${deviceData.uid}/request-battery/`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (batteryRequestError) {
        console.warn('Battery request failed:', batteryRequestError?.response?.data?.error || batteryRequestError.message);
      }

      // --- 3. Get battery, charging state + status ---
      try {
        const batteryChargingResponse = await axios.get(
          `${BASE_URL}/api/device-battery-charging/${deviceData.uid}/`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const batteryChargingData = batteryChargingResponse.data;
        setStatus(batteryChargingData);
        setChargingStatus(batteryChargingData);

        const lastSeenTime = new Date(batteryChargingData.timestamp);
        const currentTime = new Date();
        const diffInMinutes = (currentTime - lastSeenTime) / (1000 * 60);

        setDevice((prev) => ({
          ...prev,
          status: diffInMinutes <= 10 ? 'ACTIVE' : 'INACTIVE',
        }));
      } catch (batteryChargingError) {
        console.warn('No battery/charging data found:', batteryChargingError);
        setDevice((prev) => ({
          ...prev,
          status: 'UNKNOWN',
        }));
      }

      // --- 3b. Get glasses wearing status from twin state ---
      try {
        const twinResponse = await axios.get(
          `${BASE_URL}/api/iot/twin/${deviceData.uid}/`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const wearingLabels = {
          0: 'Unknown',
          1: 'Not Worn',
          2: 'Worn',
          3: 'Fault',
        };

        const display =
          twinResponse.data.wearing_display ??
          wearingLabels[twinResponse.data.glasses_wearing] ??
          null;

        setWearingDisplay(display);
      } catch (twinError) {
        console.warn('No twin state data found:', twinError);
        setWearingDisplay(null);
      }

      // --- 4. Get device location status ---
      try {
        const locationUrl = `${BASE_URL}/api/device-status-location-v2/${deviceData.uid}/`;
        const locationResponse = await axios.get(locationUrl, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const locationData = locationResponse.data;
        const latestLocation = Array.isArray(locationData)
          ? locationData.slice().sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))[0]
          : locationData;

        if (latestLocation) {
          setLocationStatus(latestLocation);
        }
      } catch (locationError) {
        console.warn('No location data found:', locationError);
      }

      // --- 6. Get device settings (auth by JWT, no UID header) ---
      const settingsResponse = await axios.get(
        `${BASE_URL}/api/preferences/device-settings/`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (
        settingsResponse.status === 200 &&
        settingsResponse.data.status === 'SUCCESS'
      ) {
        setDeviceSettings(settingsResponse.data);
      } else {
        console.warn('Device settings not found or invalid response.');
      }
    } catch (error) {
      console.error('Error fetching device info:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDeviceAndStatus();
  }, []);

  const getStatusDotClass = (status) => {
    if (status === 'ACTIVE') {
      return 'device-card-dot device-card-dot-green';
    } else if (!status || status === 'UNKNOWN') {
      return 'device-card-dot device-card-dot-red';
    } else {
      return 'device-card-dot device-card-dot-gold';
    }
  };

  const getBatteryIcon = (battery) => {
    if (battery >= 75) return <FaBatteryFull />;
    if (battery >= 50) return <FaBatteryHalf />;
    if (battery >= 25) return <FaBatteryQuarter />;
    return <FaBatteryEmpty />;
  };

  const getBatteryColor = (battery) => {
    if (battery >= 75) return '#4caf50';
    if (battery >= 50) return '#ffeb3b';
    if (battery >= 25) return '#ff9800';
    return '#f44336';
  };

  return (
    <article className="device-card-wrapper" aria-labelledby="device-info-title">
      <div className="device-card-title-bar">
        <h2 id="device-info-title" className="device-card-title">Device Information</h2>
        <button className="refresh-button" onClick={fetchDeviceAndStatus} aria-label="Refresh device information">
          <FaSyncAlt aria-hidden="true" /> Refresh
        </button>
      </div>

      {loading ? (
        <p role="status" aria-live="polite" style={{ color: '#aab7c4' }}>Loading...</p>
      ) : !device ? (
        <p role="status">No device found.</p>
      ) : (
        <div className="device-card-grid">
          <div className="device-card-container">
            <button
              className="device-card-header"
              onClick={() => navigate('/user-dashboard/settings')}
              type="button"
              aria-label="Go to settings"
            >
              <div className="device-card-id">
                <FaMicrochip aria-hidden="true" />
                <span className="uid-text">{device.serial}</span>
              </div>
              <div className="device-card-status">
                <span
                  className={getStatusDotClass(device.status)}
                  role="img"
                  aria-label={`Device status: ${device.status || 'unknown'}`}
                > </span>
                <span className="last-seen-label">Last Updated:</span>
                <span>
                  {status?.timestamp
                    ? new Date(status.timestamp).toLocaleString('en-GB')
                    : locationStatus?.timestamp
                      ? new Date(locationStatus.timestamp).toLocaleString('en-GB')
                      : 'No data'}
                </span>
              </div>
            </button>

            <div id="device-info-body" className="device-card-body">
              {/* Battery Info (if available) */}
              {status ? (
                <div className="device-card-row battery-row">
                  <div className="battery-header">
                    <strong>Battery:</strong> {status.battery}% {getBatteryIcon(status.battery)}
                  </div>
                  <div
                    className="battery-bar-container"
                    role="progressbar"
                    aria-valuenow={status.battery}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-label={`Battery level: ${status.battery}%`}
                  >
                    <div
                      className="battery-bar-fill"
                      style={{
                        width: `${status.battery}%`,
                        backgroundColor: getBatteryColor(status.battery),
                      }}
                    ></div>
                  </div>
                </div>
              ) : (
                <p>No battery data available</p>
              )}

              {/* Charging State */}
              {chargingStatus !== null && (
                <div className="device-card-row">
                  <strong>Charging:</strong> {chargingStatus.glasses_charging ? 'Yes' : 'No'}
                </div>
              )}

              {/* Wearing State */}
              {wearingDisplay !== null && (
                <div className="device-card-row">
                  <strong>Wearing:</strong> {wearingDisplay}
                </div>
              )}

              {/* show device settings if available */}
              {deviceSettings ? (
                <>
                  <div className="device-card-row">
                    <FaVolumeUp style={{ marginRight: '8px' }} aria-hidden="true" />
                    <strong>Volume:</strong>&nbsp;
                    <span>{deviceSettings.au_vol}%</span>
                  </div>

                  <div className="device-card-row">
                    <FaRulerCombined style={{ marginRight: '8px' }} aria-hidden="true" />
                    <strong>Detection Range:</strong>&nbsp;
                    <span>{deviceSettings.detection_range} mm</span>
                  </div>

                  <div className="device-card-row">
                    <strong>Buzzer Level:</strong>&nbsp;
                    <span>{deviceSettings.buzzer_level}</span>
                  </div>
                </>
              ) : (
                <p>No device settings found.</p>
              )}
            </div>

          </div>
        </div>
      )}
    </article>
  );
}

export default DeviceInfoCard;
